import React, { useEffect, useMemo, useState } from "react";
import { Button, Table, Input, Checkbox, Dropdown, Menu } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import useGetInventary from "../../hooks/getInventary/useGetInventary";
import { TimeFormatter } from "helpers/TimeFormatter/TimeFormatter";
import "./InventaryPage.css";
import useUpdateInventory from "../../hooks/updateInventory/useUpdateInventory";
import ExportTableButton from "layouts/LayoutTableExport/LayoutTableExport";
import TableSkeleton from "../../ui/table/TableSkeleton";
import { TABLE_PAGINATION_UX } from "../../ui/table/paginationDefaults";
import CopyableTruncatedCell from "../../components/CopyableTruncatedCell";

interface Inventary {
  itemId: string;
  refId: string;
  createDate: string;
  nameProduct: string;
  cantSiesa: number;
  cantVtex: number;
  diference: number;
}

const InventaryPage: React.FC = () => {
  const { inventary, loading: fetching, totalElements, fetchInventary } =
    useGetInventary();
  const { fetchUpdateInventory, renderModal } = useUpdateInventory();
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    "itemId",
    "refId",
    "cantSiesa",
    "cantVtex",
    "diference",
    "createDate",
  ]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const allColumns: TableColumnsType<Inventary> = useMemo(
    () => [
      {
        title: "Product ID",
        dataIndex: "itemId",
        key: "itemId",
        width: 120,
        render: (value: string) => (
          <CopyableTruncatedCell
            value={value}
            copySuccessMessage="Product ID copiado"
            onlyWhenTruncated={false}
          />
        ),
      },
      {
        title: "sku",
        dataIndex: "refId",
        key: "refId",
        width: 120,
        render: (value: string) => (
          <CopyableTruncatedCell
            value={value}
            copySuccessMessage="SKU copiado"
            onlyWhenTruncated={false}
          />
        ),
      },
      {
        title: "Inventario Siesa",
        dataIndex: "cantSiesa",
        key: "cantSiesa",
        width: 120,
      },
      {
        title: "Inventario Vtex",
        dataIndex: "cantVtex",
        key: "cantVtex",
        width: 120,
      },
      {
        title: "Diferencia",
        dataIndex: "diference",
        key: "diference",
        width: 100,
      },
      {
        title: "Fecha actualización",
        dataIndex: "createDate",
        key: "createDate",
        width: 140,
      },
    ],
    []
  );

  const columns: TableColumnsType<Inventary> = allColumns.filter((column) =>
    selectedColumns.includes(String(column.key))
  );

  const formattedInventary = inventary?.map((item) => ({
    ...item,
    createDate: TimeFormatter(item?.createDate),
    uniqueKey: `${item.itemId}-${item.refId}`,
  }));

  const filteredInventary = formattedInventary?.filter((item) =>
    Object?.values(item)?.some((value) =>
      value?.toString().toLowerCase().includes(searchValue?.toLowerCase())
    )
  );

  const onReload = () => {
    setLoading(true);
    fetchUpdateInventory();
    fetchInventary(pagination.current || 1, pagination.pageSize || 10).finally(
      () => setLoading(false)
    );
  };

  const handleColumnSelection = (columnKey: string, checked: boolean) => {
    setSelectedColumns((prev) =>
      checked ? [...prev, columnKey] : prev.filter((key) => key !== columnKey)
    );
  };

  const handleTableChange = (nextPagination: TablePaginationConfig) => {
    setPagination(nextPagination);
    fetchInventary(nextPagination.current || 1, nextPagination.pageSize || 10);
  };

  const columnSelectionMenu = (
    <Menu>
      {allColumns.map((col) => {
        const key = String(col.key);
        return (
          <Menu.Item key={key}>
            <Checkbox
              checked={selectedColumns.includes(key)}
              onChange={(e) => handleColumnSelection(key, e.target.checked)}
            >
              {typeof col.title === "string" ? col.title : key}
            </Checkbox>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  useEffect(() => {
    fetchInventary(pagination.current || 1, pagination.pageSize || 10);
  }, [fetchInventary, pagination]);

  const isBusy = loading || fetching;

  return (
    <>
      <div className="page__content">
        <div className="module-toolbar">
          <div className="module-toolbar__side module-toolbar__side--start">
            <div className="module-toolbar__group module-toolbar__group--actions">
              <span className="module-toolbar__label">Acciones</span>
              <div className="module-toolbar__control-row">
                <Button
                  type="primary"
                  onClick={onReload}
                  loading={isBusy}
                >
                  {isBusy ? "Actualizando" : "Actualizar"}
                </Button>
                {renderModal()}
                <ExportTableButton
                  data={filteredInventary || []}
                  columns={columns}
                  disabled={
                    isBusy ||
                    !filteredInventary ||
                    filteredInventary?.length === 0
                  }
                  variant="default"
                />
              </div>
            </div>
            <div className="module-toolbar__separator" aria-hidden="true" />
          </div>

          <div className="module-toolbar__middle">
            <div className="module-toolbar__field module-toolbar__field--search">
              <label htmlFor="inventary-search" className="module-toolbar__label">
                Valor a buscar
              </label>
              <Input
                id="inventary-search"
                className="module-toolbar__search"
                placeholder="Buscar..."
                value={searchValue}
                onChange={(e) => setSearchValue(e?.target?.value)}
                allowClear
                spellCheck={false}
                disabled={isBusy}
                suffix={
                  <SearchOutlined
                    className="module-toolbar__search-icon"
                    aria-hidden="true"
                  />
                }
              />
            </div>
          </div>

          <div className="module-toolbar__side module-toolbar__side--end">
            <div className="module-toolbar__group module-toolbar__group--columns">
              <span className="module-toolbar__label">Columnas a mostrar</span>
              <Dropdown
                overlay={columnSelectionMenu}
                trigger={["click"]}
                disabled={isBusy}
              >
                <Button
                  className="module-toolbar__columns-btn"
                  disabled={isBusy}
                >
                  Seleccionar Columnas <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>

        {fetching && (!inventary || inventary.length === 0) ? (
          <TableSkeleton />
        ) : (
          <div className="data-enter">
            <Table<Inventary>
              columns={columns}
              dataSource={filteredInventary || []}
              loading={fetching}
              rowKey="uniqueKey"
              pagination={{
                ...pagination,
                ...TABLE_PAGINATION_UX,
                total: totalElements,
                showTotal: (total) =>
                  `${total.toLocaleString("es-CO")} ${total === 1 ? "ítem" : "ítems"}`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 840 }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default InventaryPage;
